'use client';

import React from 'react';
import { motion } from 'framer-motion';
import styles from './MeetingSection.module.scss';

export interface Participant {
  name: string;
  nameKana?: string;
  title: string;
  company: string;
  description?: string;
}

export interface MeetingSectionProps {
  title: string;
  description?: string;
  participants: Participant[];
  backgroundColor?: string;
  icon?: React.ReactNode;
}

export const MeetingSection: React.FC<MeetingSectionProps> = ({
  title,
  description,
  participants,
  backgroundColor = '#f0f7ff',
  icon
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut' as const
      }
    }
  };

  return (
    <motion.section
      className={styles.meetingSection}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={containerVariants}
    >
      <motion.h2 className={styles.title} variants={itemVariants}>
        {icon && <span className={styles.icon}>{icon}</span>}
        {title}
      </motion.h2>
      
      {description && (
        <motion.p className={styles.description} variants={itemVariants}>
          {description}
        </motion.p>
      )}

      <motion.div 
        className={styles.participantsContainer}
        style={{ backgroundColor }}
        variants={itemVariants}
      >
        <h3 className={styles.participantsTitle}>参加者</h3>
        <div className={styles.participantsList}>
          {participants.map((participant, index) => (
            <motion.div
              key={index}
              className={styles.participant}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <div className={styles.participantNumber}>{index + 1}</div>
              <div className={styles.participantInfo}>
                <h4 className={styles.participantName}>
                  {participant.name}
                  {participant.nameKana && (
                    <span className={styles.nameKana}> ({participant.nameKana})</span>
                  )}
                </h4>
                <p className={styles.participantTitle}>
                  {participant.title}
                  <span className={styles.company}> / {participant.company}</span>
                </p>
                {participant.description && (
                  <p className={styles.participantDescription}>
                    {participant.description}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.section>
  );
};

export default MeetingSection;